// Screen 4a — config for the human-answered types (Retro / Poker / Mood),
// reusing the same shell as the standup config.
import React, { useState } from 'react';
import { Switch, Select, Button, Input } from 'antd';
import { MessageOutlined, PlusOutlined, CloseOutlined } from '@ant-design/icons';
import { IconTile } from './parts';

const { Option } = Select;

const BLURB = {
  retrospective:
    'People answer in Slack — Troopr collects and synthesises the responses.',
  poker: 'Estimate issues together in Slack; Troopr writes the result back to Jira.',
  mood: 'A lightweight weekly pulse. People answer in Slack and Troopr tracks the trend.',
};

const DEFAULT_QUESTIONS = {
  retrospective: [
    'What went well this sprint?',
    'What slowed us down?',
    'What should we change next sprint?',
  ],
};

export default function GenericConfig({ type, config, channels, onSave, onBack, onViewReport, saving }) {
  const c = config || {};
  const [active, setActive] = useState(c.enabled !== false);
  const [cadence, setCadence] = useState(c.cadence || 'on_demand');
  const [channel, setChannel] = useState(c.channel || (channels[0] && channels[0].id));
  const [anon, setAnon] = useState(!!c.anon);
  const [questions, setQuestions] = useState(
    c.questions || DEFAULT_QUESTIONS[type.key] || []
  );
  const [deck, setDeck] = useState(c.deck || ['1', '2', '3', '5', '8', '13', '?']);
  const [reveal, setReveal] = useState(c.reveal !== false);
  const [moodQ, setMoodQ] = useState(
    c.moodQuestion || 'How are you feeling about work this week?'
  );

  const save = () =>
    onSave({ active, cadence, channel, anon, questions, deck, reveal, moodQuestion: moodQ });

  return (
    <div className="ck21__inner">
      <span className="ck21__back" onClick={onBack}>
        ‹ All check-ins
      </span>

      <div className="ck21__cfghead">
        <IconTile type={type} large />
        <div style={{ flex: 1 }}>
          <div className="ck21__cfgname">{type.name}</div>
          <div className="ck21__cfgblurb">{BLURB[type.key]}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontWeight: 600 }}>Active</span>
          <Switch checked={active} onChange={setActive} />
        </div>
      </div>

      <div className="ck21__nlbox">
        <MessageOutlined style={{ color: '#97A0AC', fontSize: 18 }} />
        <Input placeholder={`Or just tell Troopr how to run ${type.name}…`} bordered={false} />
        <Button type="primary" style={{ background: '#244AA0', borderColor: '#244AA0' }}>
          Apply
        </Button>
      </div>

      <div className="ck21__section">
        <div className="ck21__sectitle">Schedule &amp; delivery</div>
        <div className="ck21__crow">
          <span className="ck21__clabel">Cadence</span>
          <Select value={cadence} onChange={setCadence} style={{ minWidth: 200 }}>
            <Option value="on_demand">On demand</Option>
            <Option value="weekly">Weekly</Option>
            <Option value="biweekly">Every 2 weeks</Option>
          </Select>
        </div>
        <div className="ck21__crow">
          <span className="ck21__clabel">Post responses to</span>
          <Select value={channel} onChange={setChannel} style={{ minWidth: 220 }}>
            {(channels || []).map((ch) => (
              <Option key={ch.id} value={ch.id}>
                #{ch.name}
              </Option>
            ))}
          </Select>
        </div>
      </div>

      {type.key === 'retrospective' && (
        <div className="ck21__section">
          <div className="ck21__sectitle">Questions</div>
          {questions.map((q, i) => (
            <div className="ck21__crow" key={i}>
              <Input
                value={q}
                onChange={(e) => {
                  const next = [...questions];
                  next[i] = e.target.value;
                  setQuestions(next);
                }}
                style={{ maxWidth: 560 }}
              />
              <CloseOutlined
                style={{ color: '#A6AEB8', cursor: 'pointer' }}
                onClick={() => setQuestions(questions.filter((_, j) => j !== i))}
              />
            </div>
          ))}
          <Button
            type="link"
            icon={<PlusOutlined />}
            style={{ paddingLeft: 0, color: '#5039BB' }}
            onClick={() => setQuestions([...questions, ''])}
          >
            Add question
          </Button>
          <div className="ck21__crow">
            <span className="ck21__clabel">Anonymous responses</span>
            <Switch checked={anon} onChange={setAnon} />
          </div>
        </div>
      )}

      {type.key === 'poker' && (
        <div className="ck21__section">
          <div className="ck21__sectitle">Estimation</div>
          <div className="ck21__crow">
            <span className="ck21__clabel">Deck</span>
            <div className="ck21__readchips">
              {deck.map((d) => (
                <div className="ck21__readchip ck21__readchip--on" key={d}>
                  {d}
                </div>
              ))}
            </div>
          </div>
          <div className="ck21__crow">
            <span className="ck21__clabel">Reveal votes when everyone has voted</span>
            <Switch checked={reveal} onChange={setReveal} />
          </div>
        </div>
      )}

      {type.key === 'mood' && (
        <div className="ck21__section">
          <div className="ck21__sectitle">The pulse</div>
          <div className="ck21__crow">
            <span className="ck21__clabel">Question</span>
            <Input value={moodQ} onChange={(e) => setMoodQ(e.target.value)} style={{ maxWidth: 420 }} />
          </div>
          <div className="ck21__crow">
            <span className="ck21__clabel">Scale</span>
            <div style={{ display: 'flex', gap: 8 }}>
              {['#C8413B', '#E08A2B', '#C9A227', '#5BA453', '#1B9E57'].map((c2) => (
                <div
                  key={c2}
                  style={{ width: 40, height: 40, borderRadius: 10, background: c2, opacity: 0.85 }}
                />
              ))}
            </div>
          </div>
          <div className="ck21__crow">
            <span className="ck21__clabel">Anonymous responses</span>
            <Switch checked={anon} onChange={setAnon} />
          </div>
        </div>
      )}

      <div className="ck21__footer">
        {onViewReport && (
          <Button type="link" style={{ color: '#5039BB', marginRight: 'auto' }} onClick={onViewReport}>
            View latest report →
          </Button>
        )}
        <Button onClick={onBack}>Cancel</Button>
        <Button
          type="primary"
          loading={saving}
          onClick={save}
          style={{ background: '#244AA0', borderColor: '#244AA0' }}
        >
          Save changes
        </Button>
      </div>
    </div>
  );
}
